import browser from "webextension-polyfill";
import * as ewe from "@eyeo/webext-ad-filtering-solution";
import { prepareInlineScriptForInjection } from "@eyeo/webext-ad-filtering-solution/adblockpluscore/lib/snippets.js";
import { detectionEvent } from "../shared/index.js";
import { DetectionEvents, lastBaitBlockedDateKey, lastBlockthroughTagDateKey, uboDetectedKey, uboDetectionLastRunKey, uboExtensionIds, } from "./detection.types.js";
const urlMatches = [
    { hostEquals: "www.youtube.com" },
    { hostEquals: "youtube.com" },
    { hostEquals: "www.facebook.com" },
    { hostEquals: "facebook.com" },
];
const baitsURLs = [
    "*://btloader.com/tag?*",
    "*://ad-delivery.net/px.gif?ch=1*",
    "*://ad-delivery.net/px.gif?ch=2*",
    "*://*.gpt.mail.yahoo.net/sandbox*",
    "*://cdn.adnxs.com/*/trk.js*",
    "*://*.ssl-images-amazon.com/images/S/apesafeframe/ape/sf*",
    "*://*.media-amazon.com/images/S/apesafeframe/ape/sf*",
];
async function getEventName(details, blocked) {
    var _a;
    const { url, type, tabId } = details;
    if (url.includes("btloader.com/tag")) {
        return blocked
            ? DetectionEvents.BT_LOADER_BLOCKED
            : DetectionEvents.BT_LOADER_SUCCESS;
    }
    if (url.includes("ad-delivery.net/px.gif?ch=1")) {
        return blocked
            ? DetectionEvents.AA_BAIT1_BLOCKED
            : DetectionEvents.AA_BAIT1_SUCCESS;
    }
    if (url.includes("ad-delivery.net/px.gif?ch=2")) {
        return blocked
            ? DetectionEvents.AA_BAIT2_BLOCKED
            : DetectionEvents.AA_BAIT2_SUCCESS;
    }
    try {
        const tab = await browser.tabs.get(tabId);
        const tabUrl = new URL((_a = tab.url) !== null && _a !== void 0 ? _a : "");
        const hostname = tabUrl.hostname;
        const isYahooFilter = url.includes("gpt.mail.yahoo.net/sandbox") &&
            type === "sub_frame" &&
            hostname.includes("mail.yahoo.com");
        const isMicrosoftFilter = url.includes("cdn.adnxs.com") &&
            type === "script" &&
            (hostname.includes("outlook.live.com") || hostname.includes("msn.com"));
        const isAmazonFilter = (url.includes("ssl-images-amazon.com/images/S/apesafeframe/ape/sf") ||
            url.includes("media-amazon.com/images/S/apesafeframe/ape/sf")) &&
            type === "script" &&
            hostname.includes("amazon.");
        if (isYahooFilter || isMicrosoftFilter || isAmazonFilter) {
            return blocked
                ? DetectionEvents.AA_OTHER_BLOCKED
                : DetectionEvents.AA_OTHER_SUCCESS;
        }
    }
    catch (error) { }
    return null;
}
async function getAAListsStatus() {
    const [aa, privacy] = await Promise.all([
        ewe.subscriptions.has(ewe.subscriptions.ACCEPTABLE_ADS_URL),
        ewe.subscriptions.has(ewe.subscriptions.ACCEPTABLE_ADS_PRIVACY_URL),
    ]);
    let result = 0;
    if (aa) {
        result |= 1;
    }
    if (privacy) {
        result |= 2;
    }
    return result;
}
const serverLogger = new ewe.telemetry.ServerLogger("conflict_detection");
function uboDetection(eventName) {
    const jsonIn = `{"require":{"0":{"3":{"0":{"__bbox":{"require":[{"3":{"1":{"__bbox":{"result":{"data":{"serpResponse":{"results":{"edges":[{"rendering_strategy":{"view_model":{"story":{"sponsored_data":{"ad_id":"this_will_be_removed"}}}}}]}}}}}}}}]}}}}}}`;
    const jsonOut = JSON.stringify(JSON.parse(jsonIn));
    const XHRChanged = XMLHttpRequest.toString().includes("uboLog");
    const result = jsonIn !== jsonOut || XHRChanged;
    if (!result) {
        return false;
    }
    if (eventName) {
        document.dispatchEvent(new CustomEvent(eventName));
    }
    return result;
}
async function saveResultInPrefs(prefs, key) {
    await prefs.untilLoaded;
    await prefs.set(key, Date.now());
}
async function runMV3Detection(logger, prefs, tabId) {
    try {
        const [{ result }] = await browser.scripting.executeScript({
            target: { tabId },
            func: uboDetection,
            world: "MAIN",
        });
        logger.debug("[conflict-detection]: non-partner extension MV3 detection script was injected");
        if (!result) {
            logger.debug("[conflict-detection]: NO non-partner extension was found on MV3");
            return;
        }
    }
    catch (error) {
        logger.error("[conflict-detection]: Error running MV3 detection script", error);
        return;
    }
    logger.debug("[conflict-detection]: non-partner extension found on MV3");
    await saveResultInPrefs(prefs, uboDetectedKey);
}
async function runMV2Detection(logger, tabId) {
    const executable = prepareInlineScriptForInjection(uboDetection, detectionEvent);
    try {
        await browser.tabs.executeScript(tabId, {
            frameId: 0,
            code: executable,
            matchAboutBlank: true,
            runAt: "document_end",
        });
        logger.debug("[conflict-detection]: non-partner extension MV2 detection script was injected");
    }
    catch (error) {
        logger.error("[conflict-detection]: Error running MV2 detection script", error);
    }
}
export function start(parameters) {
    const { logger, port, addTrustedMessageTypes, prefs } = parameters;
    browser.webNavigation.onCompleted.addListener(async (details) => {
        var _a, _b;
        const { tabId, frameId } = details;
        if (frameId !== 0 || tabId === -1) {
            return;
        }
        if (((_a = browser.scripting) === null || _a === void 0 ? void 0 : _a.executeScript) &&
            Object.values((_b = browser.scripting.ExecutionWorld) !== null && _b !== void 0 ? _b : {}).includes("MAIN")) {
            await runMV3Detection(logger, prefs, tabId);
        }
        else {
            await runMV2Detection(logger, tabId);
        }
        await saveResultInPrefs(prefs, uboDetectionLastRunKey);
    }, {
        url: urlMatches,
    });
    urlMatches.forEach(({ hostEquals: host }) => {
        addTrustedMessageTypes(`https://${host}`, [detectionEvent]);
    });
    port.on(detectionEvent, async () => {
        await saveResultInPrefs(prefs, uboDetectedKey);
        logger.debug("[conflict-detection]: non-partner extension found on MV2");
    });
    browser.tabs.onCreated.addListener(async (tab) => {
        const { url, pendingUrl, title } = tab;
        const tabUrl = url && url !== "" ? url : pendingUrl;
        if (!tabUrl) {
            return;
        }
        const isPartnerExtension = uboExtensionIds.some((extensionId) => tabUrl.includes(`/${extensionId}/`) || (title === null || title === void 0 ? void 0 : title.includes(extensionId)));
        if (isPartnerExtension) {
            logger.debug("[conflict-detection]: non-partner extension found by options page");
            await saveResultInPrefs(prefs, uboDetectionLastRunKey);
            await saveResultInPrefs(prefs, uboDetectedKey);
        }
    });
    browser.webRequest.onErrorOccurred.addListener(async (details) => {
        const { tabId, error } = details;
        const eventName = await getEventName(details, true);
        if (!eventName) {
            return;
        }
        const [aaListsStatus, filters] = await Promise.all([
            getAAListsStatus(),
            ewe.filters.getAllowingFilters(tabId),
        ]);
        serverLogger.info(eventName, {
            errorMsg: error,
            isPageAllowlisted: filters.length > 0,
            aaListsStatus,
        });
        if ((eventName === DetectionEvents.AA_OTHER_BLOCKED ||
            eventName === DetectionEvents.BT_LOADER_BLOCKED) &&
            aaListsStatus > 0) {
            await saveResultInPrefs(prefs, lastBaitBlockedDateKey);
        }
    }, { urls: baitsURLs });
    browser.webRequest.onCompleted.addListener(async (details) => {
        const { tabId } = details;
        const eventName = await getEventName(details, false);
        if (!eventName) {
            return;
        }
        const [aaListsStatus, filters] = await Promise.all([
            getAAListsStatus(),
            ewe.filters.getAllowingFilters(tabId),
        ]);
        serverLogger.info(eventName, {
            isPageAllowlisted: filters.length > 0,
            aaListsStatus,
        });
        if (eventName === DetectionEvents.BT_LOADER_SUCCESS) {
            await saveResultInPrefs(prefs, lastBlockthroughTagDateKey);
        }
    }, { urls: baitsURLs });
}
